import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PawPrint from "@/components/PawPrint";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";

/* ── Install PWA hook ── */
function useInstallPrompt() {
  const deferredPrompt = useRef(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Detect iOS (all browsers on iOS use WebKit — no beforeinstallprompt)
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const standalone = window.navigator.standalone === true || window.matchMedia("(display-mode: standalone)").matches;
    if (ios && !standalone) {
      setIsIOS(true);
      // Detect if current browser is Safari (vs Chrome/Firefox on iOS)
      const safari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
      setIsSafari(safari);
    }

    const handler = (e) => {
      e.preventDefault();
      deferredPrompt.current = e;
      setCanInstall(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }
    if (!deferredPrompt.current) return;
    deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    if (outcome === "accepted") setCanInstall(false);
    deferredPrompt.current = null;
  };

  return { canInstall: canInstall || isIOS, install, isIOS, isSafari, showIOSGuide, setShowIOSGuide };
}

/* ── InputField ── */
function InputField({ label, type = "text", placeholder, value, onChange, hint }) {
  return (
    <div>
      <label className="block mb-1.5 text-[13px] font-extrabold text-gray-500">
        {label}
      </label>
      <Input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      />
      {hint && (
        <div className="text-[11.5px] text-gray-400 font-semibold mt-1">
          {hint}
        </div>
      )}
    </div>
  );
}

/* ── PasswordField ── */
function PasswordField({ label, value, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block mb-1.5 text-[13px] font-extrabold text-gray-500">
        {label}
      </label>
      <div className="relative">
        <Input
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          placeholder="--------"
          className="pr-12"
        />
        <button
          type="button"
          onClick={() => setShow(v => !v)}
          className="absolute right-3.5 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-lg text-app-subtle"
        >
          {show ? "\uD83D\uDE48" : "\uD83D\uDC41\uFE0F"}
        </button>
      </div>
    </div>
  );
}

/* ── StrengthBar ── */
function StrengthBar({ password }) {
  const score = [/.{8,}/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter(r => r.test(password)).length;
  const colors = ["bg-gray-200", "bg-severity-red", "bg-severity-yellow", "bg-severity-green", "bg-severity-green"];
  const textColors = ["", "text-severity-red", "text-severity-yellow", "text-severity-green", "text-severity-green"];
  const labels = ["", "Faible", "Moyen", "Fort", "Tres fort"];
  if (!password) return null;
  return (
    <div className="mt-2">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map(i => (
          <div
            key={i}
            className={`flex-1 h-1 rounded transition-colors duration-300 ${i <= score ? colors[score] : "bg-gray-200"}`}
          />
        ))}
      </div>
      <div className={`text-[11px] font-bold mt-1 ${textColors[score]}`}>
        {labels[score]}
      </div>
    </div>
  );
}

/* ====================================================================== */
/*  LANDING                                                                */
/* ====================================================================== */
function Landing({ onLogin, onSignup }) {
  const { canInstall, install, isIOS, isSafari, showIOSGuide, setShowIOSGuide } = useInstallPrompt();

  return (
    <div className="min-h-screen flex flex-col animate-fade-in">
      {/* Top gradient section */}
      <div
        className="flex-1 flex flex-col items-center justify-center px-8 pb-12 text-center"
        style={{ background: "linear-gradient(160deg, #FFB347 0%, #FF7055 50%, #FF5C8D 100%)", paddingTop: "max(64px, calc(env(safe-area-inset-top, 0px) + 48px))" }}
      >
        <div className="animate-bounce-in">
          <PawPrint size={72} color="rgba(255,255,255,0.95)" />
        </div>
        <div className="font-display text-[42px] text-white mt-4 leading-none drop-shadow-[0_4px_16px_rgba(0,0,0,0.15)]">
          PawCare
        </div>
        <div className="text-base text-white/[0.88] font-bold mt-2.5 leading-relaxed">
          Votre veterinaire virtuel,<br />toujours la pour vos loulous
        </div>

        {/* Feature pills */}
        <div className="flex flex-col gap-2.5 mt-8 w-full max-w-[320px]">
          {[
            { icon: "\uD83E\uDE7A", text: "Consultations IA disponibles 24h/24" },
            { icon: "\uD83D\uDCCA", text: "Suivi sante & carnet vaccinal" },
            { icon: "\uD83D\uDD2C", text: "Reponses enrichies par PubMed" },
          ].map((f, i) => (
            <div
              key={i}
              className="bg-white/[0.18] backdrop-blur-sm rounded-2xl px-4 py-3 flex items-center gap-3 border border-white/25 animate-slide-up"
              style={{ animationDelay: `${200 + i * 100}ms`, animationFillMode: "both" }}
            >
              <span className="text-[22px]">{f.icon}</span>
              <span className="text-[13.5px] text-white font-bold">{f.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom white section */}
      <div className="bg-white rounded-t-5xl px-6 pt-7 pb-10 -mt-5 shadow-[0_-8px_32px_rgba(0,0,0,0.08)]">
        <Button className="w-full" onClick={onSignup}>
          <span className="text-xl">{"\uD83D\uDC3E"}</span> Creer un compte gratuit
        </Button>
        <div className="h-3" />
        <Button variant="outline" className="w-full" onClick={onLogin}>
          <span className="text-xl">{"\uD83D\uDD11"}</span> J'ai deja un compte
        </Button>

        {/* Install PWA button */}
        {canInstall && (
          <button
            onClick={install}
            className="w-full mt-3 py-3.5 rounded-2xl border-[1.5px] border-dashed border-pawcare-orange/50 bg-[#FFF8F2] cursor-pointer font-extrabold text-[14px] text-pawcare-orange-dark flex items-center justify-center gap-2.5 transition-all duration-200 hover:bg-[#FFF0E0] hover:border-pawcare-orange font-sans"
          >
            <span className="text-xl">{"\uD83D\uDCF2"}</span>
            {isIOS ? "Ajouter PawCare a l'ecran d'accueil" : "Installer l'app sur mon telephone"}
          </button>
        )}

        <p className="text-[11px] text-app-subtle text-center mt-4 font-semibold leading-relaxed">
          En continuant, vous acceptez nos{" "}
          <a href="/legal" className="text-pawcare-orange no-underline font-bold">CGU</a> et notre{" "}
          <a href="/legal?tab=privacy" className="text-pawcare-orange no-underline font-bold">politique de confidentialité</a>.
        </p>
      </div>

      {/* iOS install guide modal */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm animate-fade-in" onClick={() => setShowIOSGuide(false)}>
          <div className="bg-white rounded-t-3xl w-full max-w-[540px] p-6 pb-10 animate-slide-up" onClick={e => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mb-5" />
            <h3 className="font-black text-lg text-app-text text-center mb-4">
              {"\uD83D\uDCF2"} Installer PawCare
            </h3>

            {!isSafari && (
              <div className="bg-[#FFF4E0] border-[1.5px] border-pawcare-orange/30 rounded-2xl px-4 py-3 mb-4">
                <div className="font-extrabold text-sm text-pawcare-orange-dark mb-1">
                  {"\u26A0\uFE0F"} Ouvrez dans Safari
                </div>
                <div className="text-xs text-app-muted font-semibold leading-relaxed">
                  Sur iPhone, seul Safari permet d'ajouter une app a l'ecran d'accueil. Copiez l'adresse et ouvrez-la dans Safari, puis suivez les etapes ci-dessous.
                </div>
              </div>
            )}

            {isSafari && (
              <div className="bg-blue-50 border-[1.5px] border-blue-200 rounded-2xl px-4 py-3 mb-4 text-center">
                <div className="text-xs text-blue-600 font-bold leading-relaxed">
                  Sur iPhone, l'installation se fait manuellement via Safari en 3 etapes rapides
                </div>
              </div>
            )}

            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3.5 px-3 py-3 bg-gray-50 rounded-2xl">
                <div className="w-10 h-10 rounded-xl bg-pawcare-gradient flex items-center justify-center text-xl text-white font-black shrink-0">1</div>
                <div>
                  <div className="font-extrabold text-sm text-app-text">
                    {isSafari ? "Appuyez sur le bouton Partager" : "Ouvrez cette page dans Safari"}
                  </div>
                  <div className="text-xs text-app-muted font-semibold mt-0.5">
                    {isSafari
                      ? <span>L'icone <span className="inline-block text-blue-500 text-base align-middle">{"\uD83D\uDD03"}</span> en bas de l'ecran (carre avec fleche vers le haut)</span>
                      : "Copiez l'URL et collez-la dans Safari"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3.5 px-3 py-3 bg-gray-50 rounded-2xl">
                <div className="w-10 h-10 rounded-xl bg-pawcare-gradient flex items-center justify-center text-xl text-white font-black shrink-0">2</div>
                <div>
                  <div className="font-extrabold text-sm text-app-text">
                    {isSafari ? <>Choisissez "Sur l'ecran d'accueil"</> : "Appuyez sur le bouton Partager"}
                  </div>
                  <div className="text-xs text-app-muted font-semibold mt-0.5">
                    {isSafari
                      ? "Faites defiler la liste vers le bas si necessaire"
                      : <span>L'icone <span className="inline-block text-blue-500 text-base align-middle">{"\uD83D\uDD03"}</span> en bas de Safari</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3.5 px-3 py-3 bg-gray-50 rounded-2xl">
                <div className="w-10 h-10 rounded-xl bg-pawcare-gradient flex items-center justify-center text-xl text-white font-black shrink-0">3</div>
                <div>
                  <div className="font-extrabold text-sm text-app-text">
                    {isSafari ? <>Appuyez sur "Ajouter"</> : <>Choisissez "Sur l'ecran d'accueil"</>}
                  </div>
                  <div className="text-xs text-app-muted font-semibold mt-0.5">
                    PawCare apparaitra comme une vraie app sur votre ecran d'accueil !
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowIOSGuide(false)}
              className="w-full mt-5 py-3.5 rounded-2xl border-none cursor-pointer bg-pawcare-gradient text-white font-black text-[15px] shadow-pawcare font-sans"
            >
              J'ai compris !
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ====================================================================== */
/*  LOGIN (Connexion)                                                      */
/* ====================================================================== */
function Login({ onBack, onSuccess, onSignup }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const valid = email.includes("@") && password.length >= 6;

  const handleSubmit = async () => {
    if (!valid) return;
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      onSuccess();
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.detail || "Identifiants incorrects");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col animate-slide-up">
      {/* Header */}
      <div className="bg-pawcare-gradient px-6 pb-7" style={{ paddingTop: "max(20px, env(safe-area-inset-top, 20px))" }}>
        <button
          onClick={onBack}
          className="bg-white/[0.22] border-[1.5px] border-white/35 rounded-xl w-9 h-9 flex items-center justify-center cursor-pointer text-lg text-white mb-5"
        >
          {"\u2190"}
        </button>
        <div className="flex items-center gap-3">
          <PawPrint size={32} color="rgba(255,255,255,0.92)" />
          <div>
            <div className="font-display text-[26px] text-white leading-none">
              Bon retour !
            </div>
            <div className="text-[13px] text-white/75 font-semibold mt-0.5">
              Vos loulous vous attendent {"\uD83D\uDC3E"}
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 bg-app-gradient px-6 pt-7 pb-10 flex flex-col gap-4">
        {/* Email + Password */}
        <InputField
          label="Adresse e-mail"
          type="email"
          placeholder="vous@exemple.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
        <PasswordField
          label="Mot de passe"
          value={password}
          onChange={e => setPassword(e.target.value)}
        />

        {error && (
          <div className="bg-severity-red-bg border-[1.5px] border-red-200 rounded-[14px] px-4 py-3 text-[13px] text-severity-red font-bold">
            {"\u26A0\uFE0F"} {error}
          </div>
        )}

        <Button
          className="w-full"
          disabled={!valid || loading}
          onClick={handleSubmit}
          style={{ opacity: valid ? 1 : 0.5 }}
        >
          {loading ? (
            <>
              <span className="text-xl inline-block animate-spin">{"\uD83D\uDC3E"}</span>{" "}
              Connexion...
            </>
          ) : (
            <>
              <span className="text-xl">{"\uD83D\uDD11"}</span> Se connecter
            </>
          )}
        </Button>

        <p className="text-center text-[13.5px] text-app-muted font-bold">
          Pas encore de compte ?{" "}
          <button
            onClick={onSignup}
            className="bg-transparent border-none cursor-pointer text-pawcare-orange-dark font-black text-[13.5px] font-sans"
          >
            S'inscrire
          </button>
        </p>
      </div>
    </div>
  );
}

/* ====================================================================== */
/*  SIGNUP (Inscription)                                                   */
/* ====================================================================== */
function Signup({ onBack, onSuccess, onPending, onLogin }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [animal, setAnimal] = useState({ name: "", species: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [consent, setConsent] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const validStep1 = form.email.includes("@") && form.password.length >= 8 && form.name.trim() && consent;
  const validStep2 = animal.name.trim() && animal.species;

  const SPECIES = [
    { label: "Chat",    emoji: "\uD83D\uDC31" },
    { label: "Chien",   emoji: "\uD83D\uDC36" },
    { label: "Lapin",   emoji: "\uD83D\uDC30" },
    { label: "Oiseau",  emoji: "\uD83D\uDC26" },
    { label: "Rongeur", emoji: "\uD83D\uDC39" },
    { label: "Autre",   emoji: "\uD83D\uDC3E" },
  ];

  const handleFinish = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await register(form.name, form.email, form.password);
      if (result?.pending) {
        onPending(result.message);
        return;
      }
      onSuccess();
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la creation du compte");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col animate-slide-up">
      {/* Header */}
      <div className="bg-pawcare-gradient px-6 pb-6" style={{ paddingTop: "max(20px, env(safe-area-inset-top, 20px))" }}>
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={step === 1 ? onBack : () => setStep(1)}
            className="bg-white/[0.22] border-[1.5px] border-white/35 rounded-xl w-9 h-9 flex items-center justify-center cursor-pointer text-lg text-white"
          >
            {"\u2190"}
          </button>

          {/* Step indicators */}
          <div className="flex items-center gap-2">
            {[1, 2].map(i => (
              <div key={i} className="flex items-center gap-2">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all duration-300 ${
                    step >= i
                      ? "bg-white/90 text-pawcare-coral"
                      : "bg-white/30 text-white/60"
                  }`}
                >
                  {step > i ? "\u2713" : i}
                </div>
                {i < 2 && (
                  <div
                    className={`w-7 h-0.5 rounded transition-colors duration-300 ${
                      step > i ? "bg-white/90" : "bg-white/30"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="w-9" />
        </div>

        <div>
          <div className="font-display text-2xl text-white leading-none">
            {step === 1 ? "Creer votre compte" : "Votre premier loulou \uD83D\uDC3E"}
          </div>
          <div className="text-[12.5px] text-white/75 font-semibold mt-1">
            {step === 1
              ? "Etape 1 sur 2 -- Informations de connexion"
              : "Etape 2 sur 2 -- Ajoutez votre animal"}
          </div>
        </div>
      </div>

      {/* Form body */}
      <div className="flex-1 bg-app-gradient px-6 pt-6 pb-10 flex flex-col gap-3.5">
        {step === 1 && (
          <>
            <InputField
              label="Votre prenom"
              placeholder="Marie"
              value={form.name}
              onChange={e => set("name", e.target.value)}
            />
            <InputField
              label="Adresse e-mail"
              type="email"
              placeholder="vous@exemple.com"
              value={form.email}
              onChange={e => set("email", e.target.value)}
            />
            <div>
              <PasswordField
                label="Mot de passe"
                value={form.password}
                onChange={e => set("password", e.target.value)}
              />
              <StrengthBar password={form.password} />
            </div>

            {/* GDPR consent checkbox */}
            <div
              onClick={() => setConsent(v => !v)}
              className={`flex items-start gap-3 cursor-pointer px-3.5 py-3 rounded-[14px] border-[1.5px] transition-all duration-200 ${
                consent
                  ? "bg-pawcare-orange/[0.08] border-pawcare-orange"
                  : "bg-white/70 border-gray-200"
              }`}
            >
              <div
                className={`w-[22px] h-[22px] rounded-[7px] border-2 flex items-center justify-center shrink-0 mt-px transition-all duration-200 ${
                  consent
                    ? "border-pawcare-orange bg-pawcare-gradient"
                    : "border-gray-300 bg-white"
                }`}
              >
                {consent && (
                  <span className="text-[13px] text-white">{"\u2713"}</span>
                )}
              </div>
              <p className="text-[12.5px] text-gray-500 font-semibold leading-relaxed m-0">
                J'accepte les{" "}
                <a href="/legal" className="text-pawcare-orange no-underline font-bold" onClick={e => e.stopPropagation()}>CGU</a> et la{" "}
                <a href="/legal?tab=privacy" className="text-pawcare-orange no-underline font-bold" onClick={e => e.stopPropagation()}>politique de confidentialité</a>{" "}
                de PawCare. Mes données sont hébergées en Europe et ne sont jamais revendues.
              </p>
            </div>

            {error && (
              <div className="bg-severity-red-bg border-[1.5px] border-red-200 rounded-[14px] px-4 py-3 text-[13px] text-severity-red font-bold">
                {"\u26A0\uFE0F"} {error}
              </div>
            )}

            <Button
              className="w-full"
              disabled={!validStep1}
              onClick={() => setStep(2)}
              style={{ opacity: validStep1 ? 1 : 0.45 }}
            >
              Continuer <span className="text-lg">{"\u2192"}</span>
            </Button>

            <p className="text-center text-[13px] text-app-muted font-bold">
              Deja un compte ?{" "}
              <button
                onClick={onLogin}
                className="bg-transparent border-none cursor-pointer text-pawcare-orange-dark font-black text-[13px] font-sans"
              >
                Se connecter
              </button>
            </p>
          </>
        )}

        {step === 2 && (
          <div className="animate-slide-up">
            {/* Welcome card */}
            <div className="bg-white/70 rounded-3xl px-4 py-4 mb-1.5 border-[1.5px] border-pawcare-orange/20">
              <p className="text-[13.5px] text-gray-500 font-bold leading-relaxed">
                Bienvenue {form.name} ! {"\uD83C\uDF89"}<br />
                <span className="text-app-muted text-[12.5px]">
                  Ajoutez votre premier animal pour commencer. Vous pourrez en ajouter d'autres ensuite.
                </span>
              </p>
            </div>

            {/* Species picker */}
            <div className="mt-2">
              <label className="block mb-2.5 text-[13px] font-extrabold text-gray-500">
                Quelle est son espece ? *
              </label>
              <div className="flex flex-wrap gap-2">
                {SPECIES.map(sp => (
                  <button
                    key={sp.label}
                    onClick={() => setAnimal(p => ({ ...p, species: sp.label }))}
                    className={`px-4 py-2.5 rounded-3xl border-[1.5px] font-extrabold text-sm cursor-pointer transition-all duration-200 ${
                      animal.species === sp.label
                        ? "border-pawcare-orange bg-pawcare-orange/10 text-pawcare-orange-dark shadow-[0_3px_12px_rgba(255,179,71,0.3)]"
                        : "border-gray-200 bg-white text-app-muted"
                    }`}
                  >
                    {sp.emoji} {sp.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Animal name */}
            <div className="mt-3.5">
              <InputField
                label="Son prenom *"
                placeholder={
                  animal.species === "Chat"
                    ? "Ex : Mochi"
                    : animal.species === "Chien"
                    ? "Ex : Kouign"
                    : "Ex : Perle"
                }
                value={animal.name}
                onChange={e => setAnimal(p => ({ ...p, name: e.target.value }))}
                hint="Vous pourrez completer sa fiche apres l'inscription."
              />
            </div>

            {/* Preview card */}
            {animal.name && animal.species && (
              <div className="mt-3.5 bg-white rounded-3xl p-4 flex items-center gap-3.5 border-[1.5px] border-pawcare-orange/25 shadow-soft animate-slide-up">
                <div
                  className="w-[54px] h-[54px] rounded-full flex items-center justify-center text-[28px]"
                  style={{ background: "linear-gradient(135deg, rgba(255,179,71,0.2), rgba(255,92,141,0.15))" }}
                >
                  {SPECIES.find(s => s.label === animal.species)?.emoji || "\uD83D\uDC3E"}
                </div>
                <div>
                  <div className="font-display text-xl text-app-text">
                    {animal.name}
                  </div>
                  <div className="text-[12.5px] text-app-muted font-bold">
                    {animal.species} - Nouveau profil
                  </div>
                </div>
                <div className="ml-auto text-[22px]">{"\uD83C\uDF89"}</div>
              </div>
            )}

            {error && (
              <div className="mt-3 bg-severity-red-bg border-[1.5px] border-red-200 rounded-[14px] px-4 py-3 text-[13px] text-severity-red font-bold">
                {"\u26A0\uFE0F"} {error}
              </div>
            )}

            <div className="mt-3.5">
              <Button
                className="w-full"
                disabled={!validStep2 || loading}
                onClick={handleFinish}
                style={{ opacity: validStep2 ? 1 : 0.45 }}
              >
                {loading ? (
                  <>
                    <span className="text-xl inline-block animate-spin">{"\uD83D\uDC3E"}</span>{" "}
                    Creation du compte...
                  </>
                ) : (
                  <>
                    <span className="text-xl">{"\uD83D\uDC3E"}</span> Lancer PawCare !
                  </>
                )}
              </Button>
            </div>

            <button
              onClick={() => handleFinish()}
              className="bg-transparent border-none cursor-pointer text-center text-[13px] text-app-subtle font-bold font-sans w-full mt-2"
            >
              Passer -- j'ajouterai mon animal plus tard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ====================================================================== */
/*  PENDING APPROVAL                                                       */
/* ====================================================================== */
function Pending({ message, onBackToLogin }) {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-8 py-10 text-center animate-fade-in"
      style={{ background: "linear-gradient(160deg, #FFB347 0%, #FF7055 50%, #FF5C8D 100%)" }}
    >
      <div className="text-[80px] animate-bounce-in mb-2">{"⏳"}</div>
      <div className="font-display text-[28px] text-white mb-3 leading-tight">
        Compte en attente
      </div>
      <div className="text-[15px] text-white/[0.92] font-bold leading-relaxed mb-8 max-w-[340px]">
        {message ||
          "Votre compte a bien ete cree. Un administrateur doit l'approuver avant que vous puissiez vous connecter."}
      </div>
      <div className="w-full max-w-[320px]">
        <button
          onClick={onBackToLogin}
          className="w-full px-6 py-4 rounded-[18px] border-2 border-white/50 bg-white/[0.22] backdrop-blur-sm text-white font-black text-[17px] cursor-pointer flex items-center justify-center gap-2.5 transition-all duration-200 hover:bg-white/30 hover:scale-[1.02] active:scale-[0.98] font-sans"
        >
          Retour {"←"}
        </button>
      </div>
    </div>
  );
}

/* ====================================================================== */
/*  SUCCESS                                                                */
/* ====================================================================== */
function Success() {
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-8 py-10 text-center animate-fade-in"
      style={{ background: "linear-gradient(160deg, #FFB347 0%, #FF7055 50%, #FF5C8D 100%)" }}
    >
      <div className="text-[80px] animate-bounce-in mb-2">{"\uD83C\uDF89"}</div>
      <div className="font-display text-[32px] text-white mb-2">Bienvenue !</div>
      <div className="text-base text-white/[0.88] font-bold leading-relaxed mb-8">
        Votre compte PawCare est pret.<br />
        Vos loulous sont entre de bonnes mains {"\uD83D\uDC3E"}
      </div>
      <div className="w-full max-w-[320px]">
        <button
          onClick={() => navigate("/")}
          className="w-full px-6 py-4 rounded-[18px] border-2 border-white/50 bg-white/[0.22] backdrop-blur-sm text-white font-black text-[17px] cursor-pointer flex items-center justify-center gap-2.5 transition-all duration-200 hover:bg-white/30 hover:scale-[1.02] active:scale-[0.98] font-sans"
        >
          Decouvrir PawCare {"\u2192"}
        </button>
      </div>
    </div>
  );
}

/* ====================================================================== */
/*  MAIN WRAPPER                                                           */
/* ====================================================================== */
export default function LoginPage() {
  const [screen, setScreen] = useState("landing");
  const [pendingMessage, setPendingMessage] = useState("");

  return (
    <>
      {screen === "landing" && (
        <Landing
          onLogin={() => setScreen("login")}
          onSignup={() => setScreen("signup")}
        />
      )}
      {screen === "login" && (
        <Login
          onBack={() => setScreen("landing")}
          onSuccess={() => setScreen("success")}
          onSignup={() => setScreen("signup")}
        />
      )}
      {screen === "signup" && (
        <Signup
          onBack={() => setScreen("landing")}
          onSuccess={() => setScreen("success")}
          onPending={(msg) => {
            setPendingMessage(msg);
            setScreen("pending");
          }}
          onLogin={() => setScreen("login")}
        />
      )}
      {screen === "pending" && (
        <Pending
          message={pendingMessage}
          onBackToLogin={() => setScreen("login")}
        />
      )}
      {screen === "success" && <Success />}
    </>
  );
}
